import { useFilteredCollection } from "PFApp/use_filtered_collection";
import NotFoundComponent from "PFComponents/not_found/not_found";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import usePageWrapper from "PFCore/helpers/use_page_wrapper";
import useStorage from "PFCore/helpers/use_storage";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { usePreviousValue } from "PFCore/hooks/use_previous_value";
import { PermissionRule } from "PFTypes";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import AuditPlannerPage from "./audit_planner_page";
import ControlCentreTab from "./parts/control_centre_tab";
import ExceptionsTab from "./parts/exceptions_tab";
import { workflowStatesByTabs } from "./parts/helpers";
import ManualInterventionsTab from "./parts/manual_interventions_tab";
import { useTabs } from "./parts/use_tabs";
import useWorkflowAutomation from "./parts/use_workflow_automation";

const AuditPlannerController = () => {
  const { data: currentProfile } = useCurrentProfile();
  const isPermittedTo = useIsPermittedTo(currentProfile);
  const { formatISODate } = useDateFormatter();
  const [showPlanner, setShowPlanner] = useStorage(`auditPlannerControlCentreShowPlanner`, false);
  const workflowAutomationContext = useWorkflowAutomation();
  const { workflowAutomation } = workflowAutomationContext;
  const { t } = useTranslation("auditPlanner");

  const controlCentreEngagements = useFilteredCollection(
    "control_centre_engagements",
    "/api/audit_engagements",
    {
      requestParams: {
        perPage: 100
      },
      paramsUrl: "/control_centre",
      version: 4
    }
  );

  const controlCentreProfiles = useFilteredCollection("control_centre_profiles", "/api/profiles/index", {
    paramsUrl: "/control_centre",
    requestParams: {
      full: true,
      order_target: "capacity_index",
      order: {
        direction: "desc",
        end_date: formatISODate(moment().add(1, "months")),
        name: ["available_minutes"],
        start_date: formatISODate(),
        text: "Available minutes desc"
      }
    },
    version: 4,
    method: "POST"
  });

  const exceptionsEngagements = useFilteredCollection("exceptions_engagements", "/api/audit_engagements", {
    requestParams: {
      perPage: 100,
      filters: { fields: { workflow_state: workflowStatesByTabs.exceptions } }
    },
    paramsUrl: "/exceptions",
    version: 4
  });

  const manualInterventionsEngagements = useFilteredCollection(
    "manual_interventions_engagements",
    "/api/audit_engagements",
    {
      requestParams: {
        perPage: 100,
        filters: { fields: { workflow_state: workflowStatesByTabs.manual_interventions } }
      },
      paramsUrl: "/manual_interventions"
    }
  );

  const tabs = useMemo(
    () =>
      [
        {
          id: "control_centre",
          displayElement: t("tabs.controlCentre"),
          Component: ControlCentreTab,
          data: controlCentreEngagements,
          profiles: controlCentreProfiles,
          showPlanner,
          setShowPlanner,
          show: isPermittedTo(PermissionRule.AuditResourcer)
        },
        {
          id: "exceptions",
          displayElement: t("tabs.exceptions"),
          Component: ExceptionsTab,
          theme: { background: "var(--WorkflowState-exception)", color: "var(--Palette-background)" },
          data: exceptionsEngagements,
          show: true
        },
        {
          id: "manual_interventions",
          displayElement: t("tabs.manualInterventions"),
          Component: ManualInterventionsTab,
          theme: { background: "var(--Palette-alert)", color: "var(--Palette-text)" },
          data: manualInterventionsEngagements,
          show: isPermittedTo(PermissionRule.AuditResourcer)
        }
      ].filter(({ show }) => show),
    [
      currentProfile,
      showPlanner,
      manualInterventionsEngagements,
      exceptionsEngagements,
      controlCentreProfiles,
      controlCentreEngagements
    ]
  );

  const fetchAllEngagements = useCallback(() => {
    controlCentreEngagements.fetchData({ page: controlCentreEngagements.data.meta.page });
    exceptionsEngagements.fetchData({ page: exceptionsEngagements.data.meta.page });
    manualInterventionsEngagements.fetchData({ page: manualInterventionsEngagements.data.meta.page });
  }, [manualInterventionsEngagements, exceptionsEngagements, controlCentreEngagements]);

  const WrappedAuditPlannerPage = usePageWrapper(AuditPlannerPage);

  const { tab, changeTab } = useTabs("auditPlanner", tabs);

  const counters = tabs.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.id]: {
        count: curr.data.data.meta.total || 0,
        style: curr.theme
      }
    }),
    {}
  );

  const selectedTab = tabs.find((t) => t.id === tab) || tabs[0];
  const data = selectedTab[selectedTab.showPlanner ? "profiles" : "data"];

  const { loaded, errors } = data;

  const previousWorkflowAutomation = usePreviousValue(workflowAutomation);

  useEffect(() => {
    if (previousWorkflowAutomation) {
      fetchAllEngagements();
    }
  }, [workflowAutomation]);

  return (
    <WrappedAuditPlannerPage
      loading={!loaded}
      errors={errors}
      errorComponent={
        <NotFoundComponent message={errors ? errors.join(", ") : t("translation:pageNotFound")} />
      }
      tab={selectedTab}
      tabs={tabs}
      data={data}
      changeTab={changeTab}
      counters={counters}
      fetchAllEngagements={fetchAllEngagements}
      workflowAutomationContext={workflowAutomationContext}
    />
  );
};

export default AuditPlannerController;
